import { CheckIcon } from '@/components/common/icons';
import { SECTION_LABEL } from '@/components/common/surfaces';
import type { Category, CategorySummary } from '@/types/documents.types';
import { UNKNOWN_CATEGORY_COLOR, categoryLabel } from '@/utils/categories';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface DetectionTypesProps {
  /** The session's category snapshot with counts and the excluded flag. */
  categories: CategorySummary[];
  activeFindings: number;
  /** Findings excluded one by one (not through their category). */
  excludedFindings: number;
  /** Holds every switch (the re-run of the skipped pages is going). */
  disabled?: boolean;
  onToggle: (category: Category, excluded: boolean) => void;
}

/**
 * The review's left column, "Typy detekce": one switchable row per category with its count, then
 * the active total and what is excluded. Unticking a category is how a user says "prices are not
 * a secret in this document" without visiting every price on every page.
 */
export const DetectionTypes: FC<DetectionTypesProps> = ({
  categories,
  activeFindings,
  excludedFindings,
  disabled = false,
  onToggle,
}) => {
  const { t } = useTranslation();
  const offNames = categories
    .filter((category) => category.excluded)
    .map((category) => categoryLabel(category.key, category.label_cs));

  const summary =
    (offNames.length ? `${t('review.types.excludedTypes', { list: offNames.join(', ') })} ` : '') +
    (excludedFindings
      ? t('review.types.excludedFindings', { count: excludedFindings })
      : offNames.length
        ? ''
        : t('review.types.nothingExcluded'));

  return (
    <div className="overflow-auto border-r border-line bg-white px-4 pb-6 pt-[18px] max-[860px]:border-r-0 max-[860px]:border-b">
      <div className={`${SECTION_LABEL} mb-2.5`}>{t('review.types.title')}</div>
      {categories.map((category) => {
        const on = !category.excluded;
        const color = category.color || UNKNOWN_CATEGORY_COLOR;
        const label = categoryLabel(category.key, category.label_cs);
        return (
          <button
            key={category.key}
            type="button"
            role="checkbox"
            aria-checked={on}
            aria-label={t('review.categories.toggleLabel', { category: label })}
            disabled={disabled}
            onClick={() => onToggle(category.key, on)}
            className="-mx-2 flex w-[calc(100%+16px)] cursor-pointer items-center gap-2.5 border-none bg-transparent px-2 py-2.5 text-left font-[inherit] hover:bg-surface disabled:cursor-progress disabled:opacity-50"
          >
            <span
              className="flex h-[17px] w-[17px] flex-none items-center justify-center border-[1.5px]"
              style={{ background: on ? color : '#fff', borderColor: on ? color : '#c3d0dc' }}
            >
              <CheckIcon size={10} strokeWidth={4} stroke="#fff" style={{ opacity: on ? 1 : 0 }} />
            </span>
            <span className="inline-block h-[9px] w-[9px] flex-none" style={{ background: on ? color : '#cfd9e2' }} />
            <span className={`min-w-0 flex-1 text-[12.5px] font-medium ${on ? 'text-ink' : 'text-off line-through'}`}>
              {label}
            </span>
            <span className="flex-none text-[11.5px] font-bold" style={{ color: on ? color : '#c3d0dc' }}>
              {category.total}
            </span>
          </button>
        );
      })}
      <div className="mt-4 border-t border-hairline pt-4">
        <div className="flex items-baseline gap-[7px]">
          <span className="text-[27px] font-bold leading-none text-primary">{activeFindings}</span>
          <span className="text-[12.5px] text-subtle">{t('review.types.activeLabel')}</span>
        </div>
        <div className="mt-1.5 text-[11.5px] leading-[1.5] text-faint">{summary}</div>
      </div>
    </div>
  );
};
